// Per spec/blocks.md §big-3-hero-image (P-037.28 / D-136; reworked at
// P-037.29 / D-137 §item 6). Canvas-composite hero banner sibling to
// `big3-signature-events`'s Big 3 arm (D-132). Renders a single
// 620 × 255 PNG (1240 × 510 @2x retina per D-137 §item 5) that bakes
// the event image (620 × 160 at y=95), a 10 px navy bar (620 × 10 at
// y=85), and a circular-clipped Big 3 logo (190 × 190 horizontally
// centered at (215, 0), clipped at center (310, 95) radius 95) into
// one `<img>` tag. The logo's vertical center sits on the navy/image
// boundary line; its upper 85 px overhang above the navy bar
// (transparent canvas), middle 10 px covers the navy bar's center,
// lower 95 px overlaps the event image's top.
//
// **Composite generation lives in `src/lib/image-composite.ts` via
// `compositeBigHero()`** (the sibling helper to `compositeBig3Image`).
// Generated CLIENT-SIDE at form-edit time by `BigHeroOrchestrator`
// (`src/app/form/effects/big-hero-orchestrator.tsx`); uploaded to
// Firebase Storage at `uploads/big-3-hero-image/composites/...`; the
// resulting URL persists on the Block instance.
//
// **Two render arms:**
//
// 1. **Composite arm** (`compositeImageUrl` set, post-orchestrator):
//    single `<img>` at 620 × 255 (post-D-137; was 525 × 190 at D-136)
//    with the D-137 §item 4 inset shadow + 5 px padding ring.
//
// 2. **Auto-hide arm** (BOTH `imageUrl` AND `bigHeroLogoUrl` unset):
//    Block returns no markup. The orchestrator skips the both-empty
//    case (no point uploading a both-fallbacks composite); the Block
//    auto-hides on the same condition. Pre-orchestrator render (one
//    source set, composite not yet uploaded) → Block also returns
//    no markup until the composite lands. The compose+upload
//    completes in ~1s on broadband; staff see a moment of "blank"
//    while the orchestrator runs.
//
// **Why no fallback arm with a raw event image?** Unlike
// big3-signature-events's Big3EventCard (which has a useful 190 × 100
// fallback shape — a "regular event card without a logo"), big-3-hero-
// image's 525 × 190 hero banner is meaningless without BOTH sources —
// it's specifically the dual composite of a chapter event image AND
// the Big 3 logo. Showing a raw event image at 525 × 160 without the
// logo would look broken. Better to auto-hide until both are set + the
// composite uploads.
//
// **Image-cell edge: 1 px IMG border (D-133).**

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { image } from '../primitives/image'
import { EMPTY_HTML } from '../types'

const optionsSchema = z.object({})

const inputsSchema = z.object({
  imageUrl: z.string().url().describe('imageUrl').optional(),
  bigHeroLogoUrl: z.string().url().describe('imageUrl').optional(),
  // Derived: populated by the BigHeroOrchestrator after either source
  // changes. The `'derived'` brand hides this field from the form
  // generator — staff never sees or edits it directly. Pre-orchestrator
  // (e.g., a newly added Block) the field is undefined and the Block
  // returns EMPTY_HTML.
  compositeImageUrl: z.string().url().describe('derived').optional(),
})

// P-037.29 D-137 §item 6 — composite outer footprint 620 × 255 (was
// 525 × 190 at D-136). §item 4 (revised) — inset shadow is baked INTO
// the composite PNG on the event-image layer (before navy bar + logo
// overlays); IMG renders at full outer dimensions, no cell-side
// padding ring or box-shadow needed.
const COMPOSITE_WIDTH = 620
const COMPOSITE_HEIGHT = 255

export const big3HeroImage = defineBlock({
  id: 'big-3-hero-image',
  label: 'Big 3 Hero Image',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: {},
  // Auto-hide when BOTH sources are unset (no composite to render).
  // The orchestrator clears the stale compositeImageUrl when sources
  // transition to both-empty, so the auto-hide and the composite-clear
  // arms agree.
  autoHide: ({ inputs }) =>
    (inputs.imageUrl === undefined || inputs.imageUrl === '') &&
    (inputs.bigHeroLogoUrl === undefined || inputs.bigHeroLogoUrl === ''),
  render: ({ inputs }) => {
    if (inputs.compositeImageUrl === undefined) return EMPTY_HTML
    const compositeHtml = image({
      src: inputs.compositeImageUrl,
      alt: '',
      width: COMPOSITE_WIDTH,
      height: COMPOSITE_HEIGHT,
      align: 'center',
    })
    // 30 px L/R padded outer cell — matches event-card-group /
    // chapter-sponsors / big3-signature-events outer rhythm. Top/bottom
    // padding lives on the system-level inter-Block spacer
    // (`src/render/block-padding.ts`) per P-037.29 D-137 §item 2.
    // No cell-level box-shadow — the inset shadow is baked into the
    // composite PNG itself (D-137 §item 4 revised; see
    // `compositeBigHero` step 1b in src/lib/image-composite.ts).
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;">${compositeHtml}</td></tr></table>`
  },
})
